import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Navigation, CalendarClock, Users, Briefcase, Loader2, Check, ArrowUpRight, Copy, Route, Clock } from "lucide-react";
import { toast } from "sonner";
import { fetchEstimate, createReservation } from "@/lib/api";

const inputCls = "w-full bg-white border border-[#E6E1D8] px-4 py-3.5 text-sm text-obsidian placeholder:text-neutral-400 focus:outline-none focus:border-gold transition-colors duration-300";
const labelCls = "block text-xs uppercase tracking-[0.2em] font-medium text-neutral-500 mb-2";

export const Booking = () => {
  const [tripType, setTripType] = useState("course");
  const [form, setForm] = useState({ pickup: "", dropoff: "", datetime: "", passengers: 1, luggage: 0, hours: 2 });
  const [quote, setQuote] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [contact, setContact] = useState({ name: "", phone: "", email: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const switchType = (t) => {
    setTripType(t);
    setQuote(null);
  };

  const handleEstimate = async () => {
    if (!form.pickup.trim()) { toast.error("Indiquez le lieu de prise en charge."); return; }
    if (tripType === "course" && !form.dropoff.trim()) { toast.error("Indiquez le lieu d'arrivée."); return; }
    setEstimating(true);
    setQuote(null);
    try {
      const data = await fetchEstimate(tripType === "course"
        ? { trip_type: "course", pickup: form.pickup, dropoff: form.dropoff }
        : { trip_type: "disposition", pickup: form.pickup, hours: Number(form.hours) });
      setQuote(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'estimer cette prestation.");
    } finally {
      setEstimating(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.datetime) { toast.error("Choisissez la date et l'heure de prise en charge."); return; }
    if (!contact.name.trim() || !contact.phone.trim()) { toast.error("Nom et téléphone sont requis."); return; }
    setSubmitting(true);
    try {
      const res = await createReservation({
        name: contact.name,
        phone: contact.phone,
        email: contact.email || null,
        trip_type: tripType,
        pickup: form.pickup,
        dropoff: tripType === "course" ? form.dropoff : null,
        pickup_datetime: new Date(form.datetime).toISOString(),
        passengers: Number(form.passengers),
        luggage: Number(form.luggage),
        hours: Number(form.hours),
        notes: contact.notes || null,
      });
      setResult(res);
      toast.success("Réservation confirmée.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "La réservation a échoué. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  const tripLabel = tripType === "course"
    ? (quote?.category === "long" ? "Long trajet" : "Court trajet")
    : "Mise à disposition";

  return (
    <section id="reservation" className="py-20 lg:py-32 px-6 sm:px-12 lg:px-20 max-w-7xl mx-auto" data-testid="booking-section">
      <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8 }} className="text-xs uppercase tracking-[0.25em] font-mono text-amber-700/80 font-semibold mb-6">
        Réservation
      </motion.p>
      <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, delay: 0.1 }} className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] max-w-2xl mb-16">
        Votre course, <span className="italic text-gold">chiffrée en un instant.</span>
      </motion.h2>

      <AnimatePresence mode="wait">
        {result ? (
          <motion.div
            key="confirmation"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.215, 0.61, 0.355, 1] }}
            className="bg-obsidian text-cream p-10 lg:p-16 text-center"
            data-testid="booking-confirmation"
          >
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full border border-gold text-gold mb-8"><Check size={22} /></span>
            <h3 className="font-serif font-light text-3xl sm:text-4xl mb-4">Réservation enregistrée</h3>
            <p className="text-sm text-neutral-300 max-w-md mx-auto mb-10">
              {result.vehicle_label} · {result.trip_type === "disposition" ? `Mise à disposition ${result.hours} h` : `${result.distance_km} km`} · <span className="text-gold font-semibold">{result.price.toFixed(2)} €</span> — Conservez précieusement votre code de suivi.
            </p>
            <div className="inline-flex items-center gap-4 border border-gold/50 px-8 py-5 mb-10">
              <span className="font-mono text-2xl sm:text-3xl tracking-[0.15em] text-gold" data-testid="booking-confirmation-code">{result.code}</span>
              <button data-testid="booking-copy-code" onClick={() => { navigator.clipboard?.writeText(result.code); toast.success("Code copié."); }} className="text-neutral-400 hover:text-gold transition-colors" aria-label="Copier le code">
                <Copy size={18} />
              </button>
            </div>
            <div>
              <Link to={`/suivi?code=${result.code}`} data-testid="booking-track-link" className="group inline-flex items-center gap-2 bg-gold text-obsidian text-xs uppercase tracking-[0.2em] font-semibold px-7 py-4 hover:bg-cream transition-colors duration-300">
                Suivre ma course <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
              </Link>
            </div>
          </motion.div>
        ) : (
          <motion.div key="form" exit={{ opacity: 0, y: -16 }} className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7 space-y-8">
              <div className="grid grid-cols-2 gap-4" data-testid="booking-trip-type-toggle">
                <button
                  data-testid="booking-type-course"
                  onClick={() => switchType("course")}
                  className={`border p-5 text-left transition-all duration-300 ${tripType === "course" ? "border-gold bg-white shadow-[0_8px_30px_rgba(197,160,89,0.15)]" : "border-[#E6E1D8] bg-white/60 hover:border-gold/60"}`}
                >
                  <Route size={16} className="text-gold mb-3" />
                  <p className="text-sm font-medium text-obsidian">Course</p>
                  <p className="text-xs text-neutral-500 mt-1">Court ou long trajet · tarif fixe</p>
                </button>
                <button
                  data-testid="booking-type-disposition"
                  onClick={() => switchType("disposition")}
                  className={`border p-5 text-left transition-all duration-300 ${tripType === "disposition" ? "border-gold bg-white shadow-[0_8px_30px_rgba(197,160,89,0.15)]" : "border-[#E6E1D8] bg-white/60 hover:border-gold/60"}`}
                >
                  <Clock size={16} className="text-gold mb-3" />
                  <p className="text-sm font-medium text-obsidian">Mise à disposition</p>
                  <p className="text-xs text-neutral-500 mt-1">Chauffeur à votre service · 70 €/h</p>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls} htmlFor="pickup"><MapPin size={12} className="inline mr-1 text-gold" />Lieu de prise en charge</label>
                  <input id="pickup" data-testid="booking-pickup-location" className={inputCls} placeholder="Ex : Aéroport CDG, Roissy" value={form.pickup} onChange={set("pickup")} />
                </div>
                {tripType === "course" ? (
                  <div>
                    <label className={labelCls} htmlFor="dropoff"><Navigation size={12} className="inline mr-1 text-gold" />Lieu d'arrivée</label>
                    <input id="dropoff" data-testid="booking-dropoff-location" className={inputCls} placeholder="Ex : Hôtel Ritz, Place Vendôme, Paris" value={form.dropoff} onChange={set("dropoff")} />
                  </div>
                ) : (
                  <div>
                    <label className={labelCls} htmlFor="hours"><Clock size={12} className="inline mr-1 text-gold" />Durée (heures)</label>
                    <select id="hours" data-testid="booking-hours-select" className={inputCls} value={form.hours} onChange={set("hours")}>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n} h — {(n * 70).toFixed(0)} €</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className={labelCls} htmlFor="datetime"><CalendarClock size={12} className="inline mr-1 text-gold" />Date & heure</label>
                  <input id="datetime" data-testid="booking-datetime-picker" type="datetime-local" className={inputCls} value={form.datetime} onChange={set("datetime")} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="passengers"><Users size={12} className="inline mr-1 text-gold" />Passagers</label>
                    <select id="passengers" data-testid="booking-passengers-select" className={inputCls} value={form.passengers} onChange={set("passengers")}>
                      {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="luggage"><Briefcase size={12} className="inline mr-1 text-gold" />Bagages</label>
                    <select id="luggage" data-testid="booking-luggage-select" className={inputCls} value={form.luggage} onChange={set("luggage")}>
                      {[0, 1, 2, 3].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <button
                data-testid="booking-estimate-button"
                onClick={handleEstimate}
                disabled={estimating}
                className="w-full sm:w-auto bg-obsidian text-cream text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-gold hover:text-obsidian transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {estimating && <Loader2 size={14} className="animate-spin" />}
                {estimating ? "Calcul en cours…" : "Estimer le tarif"}
              </button>

              <AnimatePresence>
                {quote && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.6, ease: [0.215, 0.61, 0.355, 1] }} className="overflow-hidden space-y-8">
                    <div className="border border-gold bg-white p-6 flex items-center justify-between shadow-[0_8px_30px_rgba(197,160,89,0.15)]" data-testid="booking-quote">
                      <div>
                        <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-1">{tripLabel}</p>
                        <p className="text-sm text-neutral-600">
                          {quote.trip_type === "course" ? `${quote.distance_km} km · ~${quote.duration_min} min` : `${quote.hours} h de mise à disposition`}
                        </p>
                      </div>
                      <p className="font-serif text-3xl text-gold" data-testid="booking-quote-price">{quote.price.toFixed(2)} €</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className={labelCls} htmlFor="name">Nom complet</label>
                        <input id="name" data-testid="booking-name-input" className={inputCls} placeholder="Jean Dupont" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
                      </div>
                      <div>
                        <label className={labelCls} htmlFor="phone">Téléphone</label>
                        <input id="phone" data-testid="booking-phone-input" className={inputCls} placeholder="06 12 34 56 78" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
                      </div>
                      <div>
                        <label className={labelCls} htmlFor="email">Email (optionnel)</label>
                        <input id="email" data-testid="booking-email-input" type="email" className={inputCls} placeholder="jean@exemple.fr" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                      </div>
                      <div>
                        <label className={labelCls} htmlFor="notes">Précisions (optionnel)</label>
                        <input id="notes" data-testid="booking-notes-input" className={inputCls} placeholder="N° de vol, siège enfant…" value={contact.notes} onChange={(e) => setContact({ ...contact, notes: e.target.value })} />
                      </div>
                    </div>
                    <button
                      data-testid="booking-submit-button"
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="w-full bg-gold text-obsidian text-xs uppercase tracking-[0.2em] font-semibold px-8 py-5 hover:bg-obsidian hover:text-cream transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {submitting && <Loader2 size={14} className="animate-spin" />}
                      {submitting ? "Confirmation…" : `Confirmer — ${quote.price.toFixed(2)} €`}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <aside className="lg:col-span-5 bg-obsidian text-cream p-8 lg:p-10 lg:sticky lg:top-24" data-testid="booking-summary">
              <p className="text-xs uppercase tracking-[0.25em] font-mono text-gold mb-8">Récapitulatif</p>
              <dl className="space-y-5 text-sm">
                <div className="flex justify-between gap-4 border-b border-neutral-800 pb-4"><dt className="text-neutral-400">Prestation</dt><dd>{tripType === "course" ? "Course au km" : "Mise à disposition"}</dd></div>
                <div className="flex justify-between gap-4 border-b border-neutral-800 pb-4">
                  <dt className="text-neutral-400">Trajet</dt>
                  <dd className="text-right">{form.pickup || "—"}{tripType === "course" ? ` → ${form.dropoff || "—"}` : ""}</dd>
                </div>
                {tripType === "course" ? (
                  <>
                    <div className="flex justify-between gap-4 border-b border-neutral-800 pb-4"><dt className="text-neutral-400">Distance</dt><dd data-testid="summary-distance">{quote ? `${quote.distance_km} km` : "—"}</dd></div>
                    <div className="flex justify-between gap-4 border-b border-neutral-800 pb-4"><dt className="text-neutral-400">Durée estimée</dt><dd data-testid="summary-duration">{quote ? `${quote.duration_min} min` : "—"}</dd></div>
                  </>
                ) : (
                  <div className="flex justify-between gap-4 border-b border-neutral-800 pb-4"><dt className="text-neutral-400">Durée</dt><dd data-testid="summary-hours">{form.hours} h</dd></div>
                )}
                <div className="flex justify-between gap-4 border-b border-neutral-800 pb-4"><dt className="text-neutral-400">Véhicule</dt><dd>Toyota C-HR</dd></div>
                <div className="flex justify-between gap-4 border-b border-neutral-800 pb-4"><dt className="text-neutral-400">Grille tarifaire</dt><dd>{tripType === "course" ? "Tarif fixe" : "70 €/h"}</dd></div>
                <div className="flex justify-between gap-4 items-baseline pt-2"><dt className="text-neutral-400">Tarif fixe</dt><dd className="font-serif text-4xl text-gold" data-testid="summary-price">{quote ? `${quote.price.toFixed(2)} €` : "—"}</dd></div>
              </dl>
              <p className="mt-8 text-xs text-neutral-500 leading-relaxed">Tarif confirmé à la réservation. Prise en charge incluse, aucun supplément en cas de trafic.</p>
            </aside>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
